<template>
  <div class="navigation-menu-item">
    <div
      class="border-gray-lighten-2 dark:border-gray-lighten-2-dark border-t border-solid select-none"
      v-if="!item.hide"
    >
      <button
        v-bind="$attrs"
        class="flex w-full items-center justify-between py-2 disabled:cursor-not-allowed!"
        :class="[
          item.action !== null ? 'cursor-pointer' : '',
          loading ? 'cursor-wait!' : '',
          attrs.disabled || loading ? 'opacity-30' : ''
        ]"
        @click="handleClick"
      >
        <div class="mr-4 ml-2 flex h-4 w-4 flex-none items-center justify-center">
          <FontAwesomeIcon v-if="item.icon && !loading" :icon="item.icon" />
          <LoadingSpinner v-else-if="loading" class="size-4" />
        </div>
        <div class="flex w-full grow flex-wrap">
          <div class="grow text-start">
            <slot>
              <div class="font-bold">{{ item.title }}</div>
              <div class="text-xs">{{ item.subtitle }}</div>
            </slot>
          </div>
          <div v-if="slots.submenu">
            <GeneralButton
              variant="text"
              :icon="['fas', showSubmenu ? 'chevron-up' : 'chevron-down']"
            />
          </div>
        </div>
      </button>
    </div>
    <div v-if="showSubmenu">
      <slot name="submenu"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, useAttrs, useSlots } from "vue"
import LoadingSpinner from "@/components/loading/LoadingSpinner.vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"

const props = defineProps({
  item: {
    type: Object as () => {
      hide?: boolean
      icon: string | string[]
      title: string
      subtitle: string
      action: (() => void) | null
    },
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  isSubmenuVisible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  submenuVisibilityChange: [visible: boolean]
}>()

defineOptions({
  inheritAttrs: false
})

const attrs = useAttrs()
const slots = useSlots()

const internalSubmenuVisible = ref(false)

// If prop is provided, use that, otherwise use internal state
const showSubmenu = computed(() => {
  if (props.isSubmenuVisible !== false) {
    return props.isSubmenuVisible
  }
  return internalSubmenuVisible.value
})

function handleClick() {
  if (attrs.disabled || props.loading) {
    return
  }

  // If there's a submenu and no action, toggle the submenu
  if (slots.submenu && props.item.action === null) {
    internalSubmenuVisible.value = !internalSubmenuVisible.value
    emit("submenuVisibilityChange", internalSubmenuVisible.value)
  } else if (props.item.action !== null) {
    props.item.action()
  }
}
</script>

<style scoped></style>
